if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navigation: this.querySelector('.product-media-gallery__navigation'),
          navigationButtons: this.querySelectorAll('[data-gallery-direction]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isAnimating = false;

        this.elements.navigationButtons.forEach((button) => {
          button.addEventListener('click', this.onNavigationClick.bind(this));
        });
        this.updateNavigationButtons();
        this.updateNavigationPosition();

        if (this.elements.navigation) {
          this.navigationResizeObserver = new ResizeObserver(() => this.updateNavigationPosition());
          this.navigationResizeObserver.observe(this.elements.viewer);
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      onNavigationClick(event) {
        if (this.isAnimating) return;

        const mediaItems = this.getMediaItems();
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const direction = event.currentTarget.dataset.galleryDirection;
        const targetIndex = direction === 'next' ? activeIndex + 1 : activeIndex - 1;
        const targetMedia = mediaItems[targetIndex];

        if (!targetMedia) return;
        this.setActiveMedia(targetMedia.dataset.mediaId, false, direction);
      }

      updateNavigationButtons() {
        if (!this.elements.navigationButtons.length) return;

        const mediaItems = this.getMediaItems();
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-sliding-in'));
        const settledIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const currentIndex = activeIndex === -1 ? settledIndex : activeIndex;

        this.elements.navigationButtons.forEach((button) => {
          const isPrevious = button.dataset.galleryDirection === 'previous';
          button.disabled = isPrevious ? currentIndex <= 0 : currentIndex >= mediaItems.length - 1;
        });
      }

      updateNavigationPosition() {
        if (!this.elements.navigation || !this.mql.matches || this.isAnimating) return;

        requestAnimationFrame(() => {
          if (this.isAnimating) return;

          const activeMedia = this.getMediaItems().find(
            (item) => item.classList.contains('is-active') && !item.classList.contains('is-sliding-out')
          );
          const mediaContainer = activeMedia?.querySelector('.product-media-container');
          if (!mediaContainer || activeMedia.classList.contains('is-sliding-in')) return;

          const viewerRect = this.elements.viewer.getBoundingClientRect();
          const mediaRect = mediaContainer.getBoundingClientRect();
          const horizontalInset = 10;

          this.elements.navigation.style.top = `${mediaRect.top - viewerRect.top + mediaRect.height / 2}px`;
          this.elements.navigation.style.left = `${Math.max(
            horizontalInset,
            mediaRect.left - viewerRect.left + horizontalInset
          )}px`;
          this.elements.navigation.style.right = `${Math.max(
            horizontalInset,
            viewerRect.right - mediaRect.right + horizontalInset
          )}px`;
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, direction = null) {
        if (this.isAnimating) return;

        const mediaItems = this.getMediaItems();
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const previousMedia = mediaItems.find(
          (item) => item.classList.contains('is-active') && !item.classList.contains('is-sliding-item')
        ) || mediaItems.find((item) => item.classList.contains('is-active'));
        let slideDirection = direction;

        // Match old Flickity behavior: animate whenever the active media changes on desktop.
        if (
          !prepend &&
          (slideDirection !== 'next' && slideDirection !== 'previous') &&
          this.mql.matches &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          previousMedia &&
          previousMedia !== activeMedia
        ) {
          const previousIndex = mediaItems.indexOf(previousMedia);
          const nextIndex = mediaItems.indexOf(activeMedia);
          if (previousIndex !== -1 && nextIndex !== -1) {
            slideDirection = nextIndex > previousIndex ? 'next' : 'previous';
          }
        }

        const shouldAnimate =
          !prepend &&
          (slideDirection === 'next' || slideDirection === 'previous') &&
          this.mql.matches &&
          previousMedia &&
          previousMedia !== activeMedia &&
          this.dataset.desktopLayout.includes('thumbnail');

        if (shouldAnimate) {
          this.animateMediaTransition(previousMedia, activeMedia);
        } else {
          mediaItems.forEach((element) => {
            element.classList.remove('is-active', 'is-sliding-item', 'is-sliding-in', 'is-sliding-out');
            element.style.transform = '';
            element.style.transition = '';
          });
          activeMedia.classList.add('is-active');
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }
        this.updateNavigationButtons();
        if (!shouldAnimate) {
          this.updateNavigationPosition();
        }
        this.syncThumbnails(mediaId);

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Desktop thumbnail layouts hide inactive slides with display:none,
          // so horizontal scrolling can leave the gallery blank.
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });
        this.playActiveMedia(activeMedia);
      }

      animateMediaTransition(fromEl, toEl) {
        const mediaList = fromEl.parentElement;
        const mediaItems = this.getMediaItems();
        const fromIndex = mediaItems.indexOf(fromEl);
        const toIndex = mediaItems.indexOf(toEl);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

        const firstIndex = Math.min(fromIndex, toIndex);
        const lastIndex = Math.max(fromIndex, toIndex);
        const slideDistance = Math.abs(toIndex - fromIndex);
        const duration = Math.min(0.45 + Math.max(0, slideDistance - 1) * 0.1, 0.8);
        const movingItems = mediaItems.slice(firstIndex, lastIndex + 1);
        const listHeight = Math.max(fromEl.offsetHeight, toEl.offsetHeight);

        this.isAnimating = true;
        mediaList.classList.add('is-animating');
        mediaList.style.height = `${listHeight}px`;

        mediaItems.forEach((element) => {
          element.classList.remove('is-active', 'is-sliding-item', 'is-sliding-in', 'is-sliding-out');
          element.style.transform = '';
          element.style.transition = '';
          element.style.zIndex = '';
        });

        fromEl.classList.add('is-sliding-out');
        toEl.classList.add('is-sliding-in');
        movingItems.forEach((element) => {
          const itemIndex = mediaItems.indexOf(element);
          element.classList.add('is-active', 'is-sliding-item');
          element.style.transition = 'none';
          element.style.zIndex = String(100 - Math.abs(itemIndex - fromIndex));
          element.style.transform = `translate3d(${(itemIndex - fromIndex) * 100}%, 0, 0)`;
        });

        // Commit the start positions before animating (critical for reverse direction).
        void mediaList.offsetHeight;

        requestAnimationFrame(() => {
          const transition = `transform ${duration}s cubic-bezier(0.215, 0.61, 0.355, 1)`;
          movingItems.forEach((element) => {
            const itemIndex = mediaItems.indexOf(element);
            element.style.transition = transition;
            element.style.zIndex = String(100 - Math.abs(itemIndex - toIndex));
            element.style.transform = `translate3d(${(itemIndex - toIndex) * 100}%, 0, 0)`;
          });
        });

        let cleaned = false;
        const cleanup = (event) => {
          if (cleaned) return;
          if (event && event.propertyName && event.propertyName !== 'transform') return;

          cleaned = true;
          toEl.removeEventListener('transitionend', cleanup);
          movingItems.forEach((element) => {
            element.classList.remove('is-active', 'is-sliding-item', 'is-sliding-in', 'is-sliding-out');
            element.style.transform = '';
            element.style.transition = '';
            element.style.zIndex = '';
          });
          toEl.classList.add('is-active');
          mediaList.classList.remove('is-animating');
          mediaList.style.height = '';
          this.isAnimating = false;
          this.updateNavigationButtons();
          this.updateNavigationPosition();
        };

        toEl.addEventListener('transitionend', cleanup);
        window.setTimeout(() => cleanup(), duration * 1000 + 100);
      }

      syncThumbnails(mediaId) {
        this.querySelectorAll('button.thumbnail').forEach((element) => {
          element.removeAttribute('aria-current');
          element.classList.remove('active');
        });

        const activeThumbnail = this.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          const activeMedia = this.elements.viewer.querySelector(
            `.product__media-item[data-media-id="${mediaId}"]`
          );
          if (activeMedia) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!thumbnail) return;

        this.querySelectorAll('button.thumbnail').forEach((element) => {
          element.removeAttribute('aria-current');
          element.classList.remove('active');
        });

        const thumbnailButton = thumbnail.querySelector('button.thumbnail');
        if (!thumbnailButton) return;

        thumbnailButton.setAttribute('aria-current', 'true');
        thumbnailButton.classList.add('active');

        if (!this.elements.thumbnails || this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
